import {useSearchParams} from 'react-router-dom';
import {HeroSplash} from './splash/HeroSplash';
import {MARKUP_PARAM, SplashMarkup} from './splash/markup/SplashMarkup';
import {PaiInterestPage} from './splash/PaiInterestPage';

/**
 * The selectable splash looks, chosen via the `?v=` query param so a given look
 * is shareable and survives reload:
 * - `hero` (default): the dark photographic hero.
 * - `interest`: a `/cipx/intro`-style interest landing page (the destination
 *   the hero's "Express Interest" CTA navigates to).
 */
type SplashVersion = 'hero' | 'interest';

const VERSION_PARAM = 'v';
const DEFAULT_VERSION: SplashVersion = 'hero';

/** Parses the `?v=` param into a known version, falling back to the default. */
function parseVersion(value: string | null): SplashVersion {
  if (value === 'interest') return 'interest';
  return DEFAULT_VERSION;
}

/**
 * PAI splash page. Renders the {@link HeroSplash} by default, or the
 * standalone {@link PaiInterestPage} when reached via `?v=interest` (the
 * destination of the hero's "Express Interest" CTA).
 *
 * Adding the markup query param (see {@link MARKUP_PARAM}) wraps whichever
 * look is active in the {@link SplashMarkup} authoring overlay, which lets a
 * PAI admin annotate the live page with pen strokes and text boxes that
 * persist to Firestore.
 *
 * Registered as CIP's `SplashPage` override (see `main.tsx`).
 *
 * @return The selected splash look.
 * @throws Never.
 */
export function PaiSplashPage() {
  const [searchParams] = useSearchParams();
  const version = parseVersion(searchParams.get(VERSION_PARAM));

  const page = version === 'interest' ? <PaiInterestPage /> : <HeroSplash />;

  if (searchParams.has(MARKUP_PARAM)) {
    return <SplashMarkup>{page}</SplashMarkup>;
  }

  return page;
}
